// 🗺️ 치과 설정 — 「진료 후 질문」 (질문 로드맵 엔진, 2026-10-06, 플래그 roadmapEngine strict — 배포 전 OFF)
// 계약 docs/계약_질문로드맵_웹_2026-10-06.md §1 · 설계 §2-4·§3-3·§4-5
//  - 읽기 = getRoadmapSettings(대표·직원). 저장 = setRoadmapSettings(**대표만**, 서버 requireOwner). 직원은 읽기 전용(canEdit=false).
//  - mode legacy|roadmap. 켜기 전 차단(T-19): ready.phoneOk && ready.delegationOk 아니면 labels.enableBlocked + 설정 링크. 켜기 확인 창(A-5): enableConfirm + [켜기][취소].
//  - 트랙별 시점: 단계별 "며칠 뒤" 숫자만(catalog range 안 슬라이더+숫자). 문구는 catalog text 읽기 전용(고르기·편집 없음 — 2차). 잠금 트랙은 "다음 단계" 표시만.
//  - 휴진일: 요일 체크 + 날짜 목록(≤ limits.closedDatesMax). 모든 요일 휴진은 서버가 거부(closed-days-all).
//  - 변경은 새로 시작하는 트랙에만 적용 — 저장 버튼 옆 문장. 변경 이력 scheduleHistory(최근 10) 표시.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck } from 'lucide-react';
import PageHeader from '../common/PageHeader';
import { useRoadmapPilot } from '../../utils/useRoadmapPilot';   // 🗺️ 노출 = 플래그 AND 파일럿 치과(서버 위탁 확인 isPilot·confirmed·isCurrent 1.2) (2026-10-06)
import { getRoadmapSettings, setRoadmapSettings, describeRoadmapError, roadmapErrorCode, ENABLE_BLOCKED_TEXT, WEEKDAY_LABELS, fmtMs, todayKey } from '../../services/careRoadmapApi';

const TITLE = '진료 후 질문';
const LOCKED_LABEL = '다음 단계';
const APPLY_NEW_ONLY = '변경은 새로 시작하는 질문 일정에만 적용됩니다. 진행 중인 일정은 시작할 때의 시점을 그대로 씁니다.';
const CONSENT_NOT_READY = '환자 동의 문안이 아직 확정되지 않아 질문 일정을 만들 수 없습니다.';

function ClinicRoadmapSettings({ user }) {
    // 훅은 전부 최상단 — early return 위 (Dashboard 사고)
    const navigate = useNavigate();
    const businessType = user?.businessType || user?.companyBusinessType;
    const isClinic = businessType === 'clinic' || businessType === 'dental' || businessType === '치과';

    // 플래그·파일럿 판정 — loading 중에는 아무 안내도 그리지 않는다(fail-closed). flagOn=false → 미오픈 안내, flagOn && !pilotOk → 파일럿 한정 안내
    const { on: gateOn, loading: gateLoading, flagOn, pilotOk } = useRoadmapPilot(user);
    const [resp, setResp] = useState(null);          // getRoadmapSettings 응답
    const [loadErr, setLoadErr] = useState('');
    const [tracks, setTracks] = useState({});        // 편집 중 { key: { enabled, offsets:{stepId:n} } }
    const [weekly, setWeekly] = useState([]);
    const [dates, setDates] = useState([]);
    const [newDate, setNewDate] = useState('');
    const [busy, setBusy] = useState('');
    const [notice, setNotice] = useState(null);      // { type:'ok'|'err', text }
    const [confirmOn, setConfirmOn] = useState(false);

    const applyResp = (r) => {
        setResp(r);
        const s = r?.settings || {};
        const t = {};
        for (const c of (r?.catalog || [])) {
            const cur = s.tracks?.[c.key] || {};
            const offsets = {};
            for (const st of (c.steps || [])) offsets[st.stepId] = Number.isInteger(cur.offsets?.[st.stepId]) ? cur.offsets[st.stepId] : st.offsetDays;
            t[c.key] = { enabled: cur.enabled !== false && !c.locked, offsets };
        }
        setTracks(t);
        setWeekly(Array.isArray(s.closedDays?.weekly) ? s.closedDays.weekly : []);
        setDates(Array.isArray(s.closedDays?.dates) ? s.closedDays.dates : []);
    };

    useEffect(() => {
        if (!gateOn || !isClinic) return;
        let alive = true;
        getRoadmapSettings()
            .then(r => { if (alive) applyResp(r); })
            .catch((e) => { if (alive) setLoadErr(describeRoadmapError(e, '설정을 불러오지 못했습니다.')); });
        return () => { alive = false; };
    }, [gateOn, isClinic]);

    const canEdit = resp?.canEdit === true;
    const mode = resp?.settings?.mode || 'legacy';
    const ready = resp?.ready || {};
    const limits = resp?.limits || {};
    const catalog = useMemo(() => resp?.catalog || [], [resp]);
    const readyOk = ready.phoneOk === true && ready.delegationOk === true;

    if (!isClinic) {
        return (
            <div style={S.container}>
                <PageHeader icon={ClipboardCheck} title={TITLE} />
                <div style={S.emptyBox}>치과 계정만 사용할 수 있는 화면입니다.</div>
            </div>
        );
    }
    if (!gateLoading && !flagOn) {
        return (
            <div style={S.container}>
                <PageHeader icon={ClipboardCheck} title={TITLE} />
                <div style={S.emptyBox}>진료 후 질문 기능이 아직 열리지 않았습니다.</div>
            </div>
        );
    }
    // 라우트로 직접 들어온 비파일럿 치과(또는 위탁 확인 1.2 미완료·조회 실패) — 설정 조회(CF)는 하지 않고 안내만 (2026-10-06, 약관 v7.0 시행 전 파일럿 한정)
    if (!gateLoading && flagOn && !pilotOk) {
        return (
            <div style={S.container}>
                <PageHeader icon={ClipboardCheck} title={TITLE} />
                <div style={S.emptyBox}>진료 후 질문은 현재 시범 운영 중인 치과에서만 사용할 수 있습니다. 환자 앱 등록 처리위탁 확인(최신 판)이 되어 있는 시범 치과에 순차적으로 열립니다.</div>
            </div>
        );
    }

    // mode 변경 — 켜기는 확인 창(A-5) 뒤, 끄기는 바로
    const setMode = async (next) => {
        if (!canEdit || busy) return;
        setBusy('mode'); setNotice(null); setConfirmOn(false);
        try {
            const r = await setRoadmapSettings({ mode: next });
            setResp(prev => ({ ...prev, settings: r?.settings || { ...prev.settings, mode: next } }));
            setNotice({ type: 'ok', text: next === 'roadmap' ? '진료 후 질문을 켰습니다.' : '진료 후 질문을 껐습니다(이전 방식 장착 후 확인으로 돌아갑니다).' });
        } catch (e) {
            const c = roadmapErrorCode(e);
            setNotice({ type: 'err', text: (c === 'clinic-phone-required' || c === 'delegation-reconfirm-required') ? (resp?.labels?.enableBlocked || ENABLE_BLOCKED_TEXT) : describeRoadmapError(e, '저장하지 못했습니다.') });
        } finally { setBusy(''); }
    };

    const setOffset = (key, stepId, v, range) => {
        const n = Number(v);
        if (!Number.isFinite(n)) return;
        const clamped = Math.max(range[0], Math.min(range[1], Math.round(n)));
        setTracks(t => ({ ...t, [key]: { ...t[key], offsets: { ...t[key].offsets, [stepId]: clamped } } }));
    };

    const addDate = () => {
        if (!newDate || dates.includes(newDate)) { setNewDate(''); return; }
        const max = limits.closedDatesMax || 366;
        if (dates.length >= max) { setNotice({ type: 'err', text: `휴진일은 ${max}일까지 넣을 수 있습니다.` }); return; }
        setDates(d => [...d, newDate].sort());
        setNewDate('');
    };

    const save = async () => {
        if (!canEdit || busy) return;
        if (weekly.length >= 7) { setNotice({ type: 'err', text: '모든 요일을 휴진일로 둘 수 없습니다' }); return; }
        setBusy('save'); setNotice(null);
        const payloadTracks = {};
        for (const c of catalog) {
            if (c.locked || !tracks[c.key]) continue;
            payloadTracks[c.key] = { enabled: tracks[c.key].enabled === true, offsets: { ...tracks[c.key].offsets } };
        }
        try {
            const r = await setRoadmapSettings({ tracks: payloadTracks, closedDays: { weekly: [...weekly].sort((a, b) => a - b), dates: [...dates].sort() } });
            applyResp({ ...resp, settings: r?.settings || resp.settings });
            setNotice({ type: 'ok', text: (r?.changed || []).length ? `저장했습니다 (변경: ${r.changed.join(', ')}).` : '변경된 내용이 없습니다.' });
        } catch (e) {
            setNotice({ type: 'err', text: describeRoadmapError(e, '저장하지 못했습니다.') });
        } finally { setBusy(''); }
    };

    const history = Array.isArray(resp?.settings?.scheduleHistory) ? resp.settings.scheduleHistory.slice().reverse() : [];
    const trackLabel = (k) => k === 'mode' ? '사용 방식' : k === 'closedDays' ? '휴진일' : ((catalog.find(c => c.key === k) || {}).label || k);
    const fmtVal = (v) => v == null ? '-' : typeof v === 'object' ? JSON.stringify(v) : String(v);

    return (
        <div style={S.container}>
            <PageHeader icon={ClipboardCheck} title={TITLE} />

            {confirmOn && (
                <div style={S.overlay}>
                    <div style={S.modal}>
                        <div style={S.sectionTitle}>진료 후 질문 켜기</div>
                        {/* A-5 켜기 확인 문구 — getRoadmapSettings 응답 labels.enableConfirm 우선(계약 §6), 없을 때만 같은 문자열 폴백 */}
                        <div style={{ fontSize: 13.5, color: '#334155', lineHeight: 1.7 }}>{resp?.labels?.enableConfirm || '진료 후 질문을 켜면 환자의 답이 이 화면에만 전달됩니다. 답의 확인과 환자 연락은 우리 치과가 하며, 진료에 필요한 사항은 진료기록부에 직접 기록해야 합니다. 환자 화면에는 "치과가 바로 확인하지 못할 수 있으니 급하면 전화해 달라"는 안내가 표시됩니다.'}</div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                            <button type="button" disabled={!!busy} onClick={() => setConfirmOn(false)} style={S.btnSecondary}>취소</button>
                            <button type="button" disabled={!!busy} onClick={() => setMode('roadmap')} style={S.btnPrimary}>{busy === 'mode' ? '켜는 중...' : '켜기'}</button>
                        </div>
                    </div>
                </div>
            )}

            <div style={S.box}>
                <div style={S.sectionTitle}>사용 방식</div>
                {resp && !canEdit && <div style={S.readonly}>직원 계정은 확인만 할 수 있습니다. 변경은 치과 대표 계정에서 합니다.</div>}
                {loadErr && <div style={S.err}>{loadErr}</div>}
                {!resp && !loadErr ? <div style={S.help}>불러오는 중…</div> : resp && (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <span style={{ ...S.badge, background: mode === 'roadmap' ? '#eef2ff' : '#f1f5f9', color: mode === 'roadmap' ? '#4338ca' : '#64748b' }}>{mode === 'roadmap' ? '진료 후 질문 켜짐' : '이전 방식(장착 후 확인)'}</span>
                            {canEdit && mode !== 'roadmap' && (
                                readyOk
                                    ? <button type="button" disabled={!!busy} onClick={() => setConfirmOn(true)} style={S.btnPrimary}>켜기</button>
                                    : <span style={{ fontSize: 12.5, color: '#92400e' }}>{resp?.labels?.enableBlocked || resp?.labels?.phoneRequired || ENABLE_BLOCKED_TEXT} <button type="button" onClick={() => navigate('/settings')} style={{ ...S.btnSecondary, padding: '2px 8px', fontSize: 12 }}>설정으로</button></span>
                            )}
                            {canEdit && mode === 'roadmap' && <button type="button" disabled={!!busy} onClick={() => setMode('legacy')} style={S.btnSecondary}>{busy === 'mode' ? '끄는 중...' : '끄기'}</button>}
                        </div>
                        <div style={S.help}>
                            {'켜면 [진료 끝 ▾]/[장착 완료]로 시작한 질문 일정이 환자 앱으로 날짜별로 나가고, 이전 방식(기공물 도착 다음 날 추정 장착)의 자동 질문은 만들지 않습니다.'}
                            {ready.consentReady === false && <div style={{ color: '#92400e', marginTop: 4 }}>{CONSENT_NOT_READY}</div>}
                        </div>
                    </>
                )}
            </div>

            {resp && (
                <>
                    <div style={S.box}>
                        <div style={S.sectionTitle}>진료 유형별 질문 시점</div>
                        <div style={S.help}>{'문항은 덴클이 정한 목록 그대로이며 바꿀 수 없습니다. 각 단계가 기준일(장착일·진료일)로부터 며칠 뒤에 나가는지만 범위 안에서 조정합니다.'}</div>
                        {catalog.map(c => (
                            <div key={c.key} style={S.track}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{c.label}</span>
                                    <span style={S.sub}>{c.basis === 'fit_day' ? '기준일 = 장착일' : '기준일 = 진료일'}</span>
                                    {c.locked ? <span style={{ ...S.badge, background: '#f1f5f9', color: '#64748b' }}>{LOCKED_LABEL}</span> : (
                                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#0f172a', marginLeft: 'auto' }}>
                                            <input type="checkbox" disabled={!canEdit || !!busy} checked={tracks[c.key]?.enabled === true}
                                                onChange={(e) => setTracks(t => ({ ...t, [c.key]: { ...t[c.key], enabled: e.target.checked } }))} />
                                            사용
                                        </label>
                                    )}
                                </div>
                                {!c.locked && (c.steps || []).map((st, i) => {
                                    const v = tracks[c.key]?.offsets?.[st.stepId];
                                    const [min, max] = Array.isArray(st.range) ? st.range : [st.offsetDays, st.offsetDays];
                                    return (
                                        <div key={st.stepId} style={S.step}>
                                            <div style={{ fontSize: 13, color: '#334155' }}><span style={{ color: '#94a3b8', marginRight: 6 }}>{`${i + 1}단계`}</span>{st.text}</div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                                                <input type="range" min={min} max={max} step={1} value={Number.isInteger(v) ? v : st.offsetDays} disabled={!canEdit || !!busy}
                                                    onChange={(e) => setOffset(c.key, st.stepId, e.target.value, [min, max])} style={{ width: 160 }} aria-label={`${c.label} ${i + 1}단계 며칠 뒤`} />
                                                <input type="number" min={min} max={max} step={1} value={Number.isInteger(v) ? v : st.offsetDays} disabled={!canEdit || !!busy}
                                                    onChange={(e) => setOffset(c.key, st.stepId, e.target.value, [min, max])} style={S.num} />
                                                <span style={S.sub}>{`일 뒤 (${min}~${max}일 · 기본 ${st.offsetDays}일)`}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                        {limits.maxSendsPerTrack && <div style={S.help}>{`최대 ${limits.maxSendsPerTrack}회 발송(재발송·재질문 포함) · 질문 사이 최소 ${limits.minGapDays}일 · 기준일부터 최대 ${limits.maxSpanDays}일(서버가 강제).`}</div>}
                    </div>

                    <div style={S.box}>
                        <div style={S.sectionTitle}>휴진일</div>
                        <div style={S.help}>{'휴진일에는 질문을 보내지 않고 다음 진료일로 미룹니다.'}</div>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
                            {WEEKDAY_LABELS.map((w, i) => (
                                <label key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#0f172a' }}>
                                    <input type="checkbox" disabled={!canEdit || !!busy} checked={weekly.includes(i)}
                                        onChange={(e) => setWeekly(ws => e.target.checked ? [...ws, i].sort((a, b) => a - b) : ws.filter(x => x !== i))} />
                                    {w}
                                </label>
                            ))}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                            <input type="date" value={newDate} min={todayKey()} disabled={!canEdit || !!busy} onChange={(e) => setNewDate(e.target.value)} style={S.date} aria-label="휴진 날짜 추가" />
                            <button type="button" disabled={!canEdit || !!busy || !newDate} onClick={addDate} style={S.btnSecondary}>날짜 추가</button>
                            <span style={S.sub}>{`${dates.length}일 (최대 ${limits.closedDatesMax || 366}일)`}</span>
                        </div>
                        {dates.length > 0 && (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                                {dates.map(d => (
                                    <span key={d} style={{ ...S.badge, background: '#f1f5f9', color: '#334155', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                        {d.replace(/-/g, '.')}
                                        {canEdit && <button type="button" disabled={!!busy} onClick={() => setDates(ds => ds.filter(x => x !== d))} aria-label={`${d} 삭제`} style={{ border: 'none', background: 'transparent', color: '#64748b', cursor: 'pointer', padding: 0, fontSize: 13 }}>×</button>}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <div style={S.box}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <button type="button" disabled={!canEdit || !!busy} onClick={save} style={{ ...S.btnPrimary, opacity: canEdit ? 1 : 0.5 }}>{busy === 'save' ? '저장 중...' : '저장'}</button>
                            <span style={{ fontSize: 12.5, color: '#64748b' }}>{APPLY_NEW_ONLY}</span>
                        </div>
                        {notice && <div style={notice.type === 'ok' ? S.ok : S.err}>{notice.text}</div>}
                    </div>

                    <div style={S.box}>
                        <div style={S.sectionTitle}>변경 이력 (최근 10건)</div>
                        {history.length === 0 ? <div style={S.help}>아직 변경 이력이 없습니다.</div> : history.map((h, i) => (
                            <div key={`${h.at}-${i}`} style={{ fontSize: 12.5, color: '#334155', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                                <span style={{ color: '#94a3b8', marginRight: 8 }}>{fmtMs(h.at, true)}</span>
                                <span style={{ marginRight: 8 }}>{h.role === 'owner' ? '대표' : h.role || ''}</span>
                                <span style={{ fontWeight: 600, marginRight: 8 }}>{trackLabel(h.track)}</span>
                                <span style={{ color: '#64748b', wordBreak: 'break-all' }}>{`${fmtVal(h.before)} → ${fmtVal(h.after)}`}</span>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

const S = {
    container: { padding: 20, maxWidth: 760, margin: '0 auto' },
    emptyBox: { border: '1px solid #e2e8f0', borderRadius: 12, padding: 26, textAlign: 'center', fontSize: 14, color: '#9ca3af', background: '#fff' },
    box: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px', marginBottom: 14 },
    sectionTitle: { fontSize: 14.5, fontWeight: 700, color: '#0f172a', marginBottom: 10 },
    help: { fontSize: 12.5, color: '#64748b', lineHeight: 1.6, marginTop: 10 },
    readonly: { fontSize: 12.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 11px', marginBottom: 10 },
    sub: { fontSize: 12, color: '#94a3b8' },
    badge: { display: 'inline-block', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600 },
    track: { borderTop: '1px solid #f1f5f9', padding: '12px 0' },
    step: { padding: '6px 0 6px 10px', borderLeft: '2px solid #e2e8f0', marginTop: 8 },
    num: { width: 64, padding: '4px 6px', fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 6 },
    date: { padding: '5px 8px', fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 6 },
    btnPrimary: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    btnSecondary: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', backgroundColor: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
    modal: { background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 18, width: '100%', maxWidth: 480 },
    ok: { marginTop: 10, background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', borderRadius: 8, padding: '8px 11px', fontSize: 12.5 },
    err: { marginTop: 10, background: '#fdecec', border: '1px solid #f5cfcf', color: '#8a2020', borderRadius: 8, padding: '8px 11px', fontSize: 12.5 },
};

export default ClinicRoadmapSettings;
